import { Box, Button, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import DataGrid from '../../shared/DataGrid/StyledDataGrid';
import { DataGridContainer } from '../RestaurantsPage/tabs/Info';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { StyledRouterLink } from '../../shared/StyledLink/StyledLink';
import { useAllIngredients, useAllProductMenus, useAllProducts } from '../../apis/products-api';
import { checkPermissions, PRODUCTS_WRITE } from '../../shared/utils/permissions';
import { useUserPermissions } from '../../apis/user-details';
import { TopBar } from '../ProductsPage';
import ProductMenuDetails from './ProductMenuDetails';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import WarningIcon from '@mui/icons-material/Warning';

type Row = {
  id: string;
  displayName: string;
  products: string;
  restaurants: string;
  description: string;
  active: boolean;
  activeProductsCount: number;
  hasDeactivatedProducts: boolean;
  activeIngredientsCount: number;
  hasDeactivatedIngredients: boolean;
};

const ProductMenusPage = () => {
  const { data: menus = [] } = useAllProductMenus();
  const { roles: userPermissions } = useUserPermissions();
  const { data: products } = useAllProducts();
  const { data: ingredients } = useAllIngredients();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCreateNewMenu = () => {
    navigate('/product-menus/new');
  };

  const extendedMenuData = menus.map((menu) => {
    const menuProducts = menu.productIds?.map((product) => {
      return products?.find((p) => p.productId === product);
    });
    const activeProductsCount = menuProducts?.filter((product) => {
      return product?.active === true;
    }).length;

    const hasDeactivatedProducts =
      (activeProductsCount && activeProductsCount < 1) || activeProductsCount !== menuProducts?.length;

    const menuIngredients = menu.ingredientIds?.map((ingredient) => {
      return ingredients?.find((p) => p.ingredientId === ingredient);
    });
    const activeIngredientsCount = menuIngredients?.filter((ingredient) => {
      return ingredient?.isActive === true;
    }).length;

    const hasDeactivatedIngredients =
      (activeIngredientsCount && activeIngredientsCount < 1) || activeIngredientsCount !== menuIngredients?.length;

    return {
      ...menu,
      activeProductsCount: activeProductsCount,
      hasDeactivatedProducts: hasDeactivatedProducts,
      activeIngredientsCount: activeIngredientsCount,
      hasDeactivatedIngredients: hasDeactivatedIngredients,
    };
  });

  const columns: GridColDef<Row>[] = [
    {
      field: 'displayName',
      headerName: t('productMenus.list.name'),
      flex: 2,
      renderCell(params) {
        return <StyledRouterLink to={params.row.id}>{params.row.displayName}</StyledRouterLink>;
      },
    },
    {
      field: 'products',
      headerName: t('productMenus.list.products'),
      flex: 1.5,
      renderCell: (params) => {
        return params.row.hasDeactivatedProducts ? (
          <>
            <WarningIcon color="warning" />
            {params.row.activeProductsCount}/{params.value} {t('As active')}
          </>
        ) : (
          <>
            <CheckBoxIcon color="success" /> {params.value} {t('As active')}
          </>
        );
      },
    },
    {
      field: 'ingredients',
      headerName: t('ingredients.title'),
      flex: 1,
      renderCell: (params) => {
        return params.value == 0 ? null : params.row.hasDeactivatedIngredients ? (
          <>
            <WarningIcon color="warning" /> {params.row.activeIngredientsCount}/{params.value} {t('As active')}
          </>
        ) : (
          <>
            <CheckBoxIcon color="success" /> {params.value} {t('As active')}
          </>
        );
      },
    },
    {
      field: 'restaurants',
      headerName: t('productMenus.list.restaurants'),
      flex: 1,
    },
    {
      field: 'description',
      headerName: t('productMenus.list.description'),
      flex: 4,
    },
    {
      field: 'active',
      headerName: '',
      flex: 1,
      align: 'right',
      renderCell: (params) => {
        return (params.value as boolean) ? (
          <Chip color="primary" label={t('shared.activeLabel.active')} />
        ) : (
          <Chip color="error" label={t('shared.activeLabel.inactive')} />
        );
      },
    },
  ];

  const rows: Row[] = extendedMenuData.map((r) => ({
    id: r.productMenuId?.toString() || '',
    displayName: r.name,
    ingredients: r.ingredientIds?.length?.toString() || 0,
    products: r.productIds?.length?.toString() || '0',
    restaurants: r.shopsHavingMenu?.length ? `${r.shopsHavingMenu.length} kpl` : '0',
    description: r.description || '',
    active: r.isActive || false,
    activeProductsCount: r.activeProductsCount || 0,
    hasDeactivatedProducts: r.hasDeactivatedProducts || false,
    activeIngredientsCount: r.activeProductsCount || 0,
    hasDeactivatedIngredients: r.hasDeactivatedIngredients || false,
  }));

  return (
    <Routes>
      <Route path=":id" element={<ProductMenuDetails />} />
      <Route
        index
        element={
          <Box>
            <TopBar>
              <Typography variant="h1">{t('productMenus.list.title')}</Typography>
              <Button
                disabled={!checkPermissions(userPermissions || [], [PRODUCTS_WRITE])}
                variant="contained"
                onClick={handleCreateNewMenu}
                color="primary"
              >
                {t('productMenus.addMenu')}
              </Button>
            </TopBar>
            <DataGridContainer>
              <DataGrid columns={columns} rows={rows} />
            </DataGridContainer>
          </Box>
        }
      />
    </Routes>
  );
};

export default ProductMenusPage;
